import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import './NavBar.css';

const NavBar = () => {
  const { language, switchLanguage, t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const headerRef = useRef(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const apply = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--nav-drawer-top', `${h}px`);
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    window.addEventListener('resize', apply);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', apply);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const navLinkClass = 'nav-link';
  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <header className="nav-header" ref={headerRef}>
        <div className="nav-header-bg" aria-hidden="true" />
        <div className="nav-header-content">
          <nav className="navbar" aria-label="Main">
            <div className="nav-inner section-shell">
              <Link to="/#hero" className="nav-brand" onClick={closeMenu}>
                <span className="nav-brand-mark" aria-hidden />
                <span className="nav-brand-text">{t.hero.name.split(' ')[0]}</span>
              </Link>

              <button
                type="button"
                className={`nav-toggle ${menuOpen ? 'is-open' : ''}`}
                onClick={() => setMenuOpen((o) => !o)}
                aria-expanded={menuOpen}
                aria-controls="primary-navigation"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              >
                <span className="nav-toggle-bar" />
                <span className="nav-toggle-bar" />
                <span className="nav-toggle-bar" />
              </button>

              <ul
                id="primary-navigation"
                className={`nav-list ${menuOpen ? 'is-open' : ''}`}
              >
                <li><Link className={navLinkClass} to="/#hero" onClick={closeMenu}>{t.nav.home}</Link></li>
                <li><Link className={navLinkClass} to="/#about" onClick={closeMenu}>{t.nav.about}</Link></li>
                <li><Link className={navLinkClass} to="/#tech" onClick={closeMenu}>{t.nav.tech}</Link></li>
                <li><Link className={navLinkClass} to="/#embedded" onClick={closeMenu}>{t.nav.embedded}</Link></li>
                <li><Link className={navLinkClass} to="/#experience" onClick={closeMenu}>{t.nav.experience}</Link></li>
                <li><Link className={navLinkClass} to="/#projects" onClick={closeMenu}>{t.nav.projects}</Link></li>
                <li><Link className={navLinkClass} to="/#contact" onClick={closeMenu}>{t.nav.contact}</Link></li>
                <li><Link className={navLinkClass} to="/now" onClick={closeMenu}>{t.nav.now}</Link></li>
                <li className="nav-lang">
                  <button
                    type="button"
                    className={language === 'en' ? 'lang-btn active' : 'lang-btn'}
                    onClick={() => switchLanguage('en')}
                    aria-pressed={language === 'en'}
                  >
                    {t.lang.en}
                  </button>
                  <span className="lang-sep" aria-hidden>|</span>
                  <button
                    type="button"
                    className={language === 'de' ? 'lang-btn active' : 'lang-btn'}
                    onClick={() => switchLanguage('de')}
                    aria-pressed={language === 'de'}
                  >
                    {t.lang.de}
                  </button>
                </li>
              </ul>
            </div>
          </nav>
        </div>
      </header>
      <button
        type="button"
        className={`nav-backdrop ${menuOpen ? 'is-visible' : ''}`}
        aria-hidden={!menuOpen}
        tabIndex={-1}
        onClick={closeMenu}
      />
    </>
  );
};

export default NavBar;
